import { usePaymentAnalytics } from './usePaymentAnalytics';
import { useEffect, useRef } from 'react';
import { ANALYTICS_EVENTS, AnalyticsProperties } from '../lib/analytics';

/** Observe the payment document without interpreting hiding as a bank launch or a tab close. */
export const usePaymentPageLifecycle = (properties: AnalyticsProperties) => {
    const trackAnalyticsEvent = usePaymentAnalytics();
    const latestProperties = useRef(properties);
    latestProperties.current = properties;

    useEffect(() => {
        const startedAt = performance.now();
        let visibleSince: number | null = document.visibilityState === 'visible' ? startedAt : null;
        let hiddenSince: number | null = visibleSince === null ? startedAt : null;
        let visibleDuration = 0;
        let pageLeft = false;

        const emit = (
            eventName: Parameters<typeof trackAnalyticsEvent>[0],
            extra: AnalyticsProperties,
            flushImmediately = false,
        ) => {
            const now = performance.now();
            trackAnalyticsEvent(eventName, {
                ...latestProperties.current,
                time_on_page_ms: Math.round(now - startedAt),
                visible_duration_ms: Math.round(visibleDuration + (visibleSince === null ? 0 : now - visibleSince)),
                ...extra,
            }, { flushImmediately });
        };

        const updateVisibility = (source: string) => {
            if (pageLeft) return;
            const now = performance.now();
            if (document.visibilityState === 'hidden' && visibleSince !== null) {
                visibleDuration += now - visibleSince;
                visibleSince = null;
                hiddenSince = now;
                emit(ANALYTICS_EVENTS.PAYMENT_PAGE_HIDDEN, { source }, true);
            } else if (document.visibilityState === 'visible' && visibleSince === null) {
                const hiddenDuration = hiddenSince === null ? 0 : now - hiddenSince;
                visibleSince = now;
                hiddenSince = null;
                emit(ANALYTICS_EVENTS.PAYMENT_PAGE_VISIBLE, {
                    source,
                    hidden_duration_ms: Math.round(hiddenDuration),
                });
            }
        };
        const onVisibilityChange = () => updateVisibility('visibilitychange');
        const onPageHide = (event: PageTransitionEvent) => {
            if (pageLeft) return;
            pageLeft = true;
            const now = performance.now();
            if (visibleSince !== null) {
                visibleDuration += now - visibleSince;
                visibleSince = null;
                hiddenSince = now;
            }
            // pagehide can mean navigation, reload, or closure. persisted means
            // the browser may retain the document in its back/forward cache.
            emit(ANALYTICS_EVENTS.PAYMENT_PAGE_LEFT, { source: 'pagehide', persisted: event.persisted }, true);
        };
        const onPageShow = (event: PageTransitionEvent) => {
            if (!event.persisted || !pageLeft) return;
            pageLeft = false;
            emit(ANALYTICS_EVENTS.PAYMENT_PAGE_RESTORED, { source: 'pageshow', persisted: true });
            updateVisibility('pageshow');
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('pageshow', onPageShow);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('pageshow', onPageShow);
            // React cleanup is not evidence that a browser page was closed.
        };
    }, [properties.payment_id, trackAnalyticsEvent]);
};
